// SPDX-License-Identifier: AGPL-3.0-or-later

import i18n, {loadLocaleCatalog, normalizeLocale} from '@app/app/I18n';
import {getCachedCollator} from '@app/features/i18n/utils/IntlCache';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as UserSettingsCommands from '@app/features/user/commands/UserSettingsCommands';
import UserSettings from '@app/features/user/state/UserSettings';
import {DiscoverySupportedLanguages} from '@fluxer/constants/src/DiscoveryConstants';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';

const ENGLISH_DESCRIPTOR = msg({
	message: 'English',
	comment: 'Short label in the language and locale names. Keep it concise.',
});
const ENGLISH_US_DESCRIPTOR = msg({
	message: 'English (US)',
	comment: 'Short label in the language and locale names. Keep it concise.',
});
const logger = new Logger('LocaleUtils');

interface LocaleInfo {
	code: string;
	name: MessageDescriptor;
	nativeName: string;
	flag: string;
	region?: string;
}

const SUPPORTED_LOCALES: Array<LocaleInfo> = [
	{code: 'en-GB', name: ENGLISH_DESCRIPTOR, nativeName: 'English', flag: '🇬🇧'},
	{code: 'en-US', name: ENGLISH_US_DESCRIPTOR, nativeName: 'English (US)', flag: '🇺🇸'},
];
const DEFAULT_LOCALE = 'en-US';

let localeChangeSequence = 0;
let localLocaleOverride: string | null = null;

export function getCurrentLocale(): string {
	return UserSettings.getLocale() || DEFAULT_LOCALE;
}

export function getCurrentOrDetectedLocale(): string {
	if (localLocaleOverride) {
		return localLocaleOverride;
	}
	const currentLocale = UserSettings.getLocale();
	if (UserSettings.isHydrated() || currentLocale !== DEFAULT_LOCALE) {
		return currentLocale || DEFAULT_LOCALE;
	}
	return normalizeLocale(i18n.locale || DEFAULT_LOCALE);
}

export function setLocale(localeCode: string): void {
	const locale = SUPPORTED_LOCALES.find((supportedLocale) => supportedLocale.code === localeCode);
	if (!locale) {
		logger.warn(`Unsupported locale: ${localeCode}`);
		return;
	}
	const normalized = normalizeLocale(locale.code);
	const previousLocale = normalizeLocale(getCurrentLocale());
	if (normalized === previousLocale) {
		return;
	}
	const sequence = ++localeChangeSequence;
	void (async () => {
		try {
			await loadLocaleCatalog(normalized);
		} catch (error) {
			logger.error(`Failed to load locale ${localeCode}:`, error);
			return;
		}
		if (sequence !== localeChangeSequence) {
			return;
		}
		try {
			await UserSettingsCommands.update({
				locale: normalized,
			});
		} catch (error) {
			logger.error(`Failed to persist locale ${localeCode}:`, error);
			if (sequence !== localeChangeSequence) {
				return;
			}
			try {
				await loadLocaleCatalog(previousLocale);
			} catch (restoreError) {
				logger.error(`Failed to restore locale ${previousLocale}:`, restoreError);
			}
		}
	})();
}

export function setLocalLocale(localeCode: string): void {
	const locale = SUPPORTED_LOCALES.find((supportedLocale) => supportedLocale.code === localeCode);
	if (!locale) {
		logger.warn(`Unsupported locale: ${localeCode}`);
		return;
	}
	const normalized = normalizeLocale(locale.code);
	localLocaleOverride = normalized;
	const sequence = ++localeChangeSequence;
	UserSettings.applyLocalLocale(normalized);
	void (async () => {
		try {
			await loadLocaleCatalog(normalized);
		} catch (error) {
			logger.error(`Failed to load locale ${localeCode}:`, error);
			return;
		}
		if (sequence !== localeChangeSequence) {
			return;
		}
		UserSettings.applyLocalLocale(normalized);
	})();
}

interface TranslatedLocaleInfo {
	code: string;
	name: string;
	nativeName: string;
	flag: string;
	region?: string;
}

interface DiscoveryLanguageInfo {
	code: string;
	label: string;
}

export function getLocalizedLocaleName(code: string): string {
	const info = SUPPORTED_LOCALES.find((locale) => locale.code === code);
	return info ? i18n._(info.name) : code;
}

export function getDiscoveryLanguageLabel(code: string): string {
	if (code === 'en-US') {
		return i18n._(ENGLISH_DESCRIPTOR);
	}
	return getLocalizedLocaleName(code);
}

export function getSortedDiscoveryLanguages(): Array<DiscoveryLanguageInfo> {
	const collator = getCachedCollator(i18n.locale || undefined, {sensitivity: 'base'});
	return DiscoverySupportedLanguages.map((language) => ({
		code: language.code,
		label: getDiscoveryLanguageLabel(language.code),
	})).sort((a, b) => collator.compare(a.label, b.label) || a.code.localeCompare(b.code));
}

export function getSortedLocales(): Array<TranslatedLocaleInfo> {
	return [...SUPPORTED_LOCALES]
		.map((locale) => ({
			...locale,
			name: i18n._(locale.name),
		}))
		.sort((a, b) => a.code.localeCompare(b.code));
}
